// Native HTML5 drag-to-transition for the project board (Screen 3 states 1–5).
import { useCallback, useRef, useState } from "react";
import {
  getProjectJiraTransitions,
  projectJiraAction,
  type JiraTransitionOption,
  type ProjectJiraIssueRow,
} from "../../../lib/brain-api";

export type BoardColumnKey = "new" | "indeterminate" | "done";

export type BoardIssue = ProjectJiraIssueRow & {
  /** Transient label while a move is in flight / awaiting ambiguous pick. */
  displayStatus?: string;
};

export type AmbiguousPending = {
  key: string;
  targetCat: BoardColumnKey;
  options: JiraTransitionOption[];
  origin: BoardIssue;
};

export type FailedDrop = {
  key: string;
  targetCat: BoardColumnKey;
  message: string;
};

export type BoardToast = {
  text: string;
  tone: "ok" | "bad";
  retry?: FailedDrop | null;
};

const COLUMN_LABEL: Record<BoardColumnKey, string> = {
  new: "To Do",
  indeterminate: "In Progress",
  done: "Done",
};

/** Prefer Jira's category; fall back to name heuristics + board-known map. */
export function transitionMatchesColumn(
  t: JiraTransitionOption,
  targetCat: BoardColumnKey,
  statusCatByName: Map<string, string>
): boolean {
  const fromApi = (t.toStatusCategory ?? "").toLowerCase();
  if (fromApi === "new" || fromApi === "indeterminate" || fromApi === "done") {
    return fromApi === targetCat;
  }
  const known = statusCatByName.get(t.toStatus.trim().toLowerCase());
  if (known) return known === targetCat;
  const s = t.toStatus.trim().toLowerCase();
  if (/^(done|complete|closed|resolved|cancelled|canceled)$/.test(s) || /\b(done|complete)\b/.test(s)) {
    return targetCat === "done";
  }
  if (
    /\b(progress|review|develop|qa|test|doing|blocked|waiting)\b/.test(s) ||
    s === "in progress" ||
    s === "in review"
  ) {
    return targetCat === "indeterminate";
  }
  return targetCat === "new";
}

function buildStatusCatMap(issues: BoardIssue[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const i of issues) {
    const cat = String(i.statusCategory ?? "").toLowerCase();
    if (cat) m.set(i.status.trim().toLowerCase(), cat);
  }
  return m;
}

export function useBoardDnd({
  projectId,
  issues,
  setIssues,
  onToast,
}: {
  projectId: string;
  issues: BoardIssue[];
  setIssues: React.Dispatch<React.SetStateAction<BoardIssue[]>>;
  onToast: (opts: BoardToast) => void;
}) {
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<BoardColumnKey | null>(null);
  const [settling, setSettling] = useState<string | null>(null);
  const [pending, setPending] = useState<AmbiguousPending | null>(null);
  const toastRef = useRef(onToast);
  toastRef.current = onToast;
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const patchIssue = useCallback(
    (key: string, patch: Partial<BoardIssue>) => {
      setIssues((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));
    },
    [setIssues]
  );

  const restoreOrigin = useCallback(
    (origin: BoardIssue) => {
      setIssues((prev) =>
        prev.map((i) => (i.key === origin.key ? { ...origin, displayStatus: undefined } : i))
      );
    },
    [setIssues]
  );

  const clearSettling = useCallback(() => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = null;
    setSettling(null);
  }, []);

  /** Settle chrome starts on optimistic apply (900ms), not after Jira confirms. */
  const markSettling = useCallback((key: string, ms = 900) => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    setSettling(key);
    settleTimer.current = setTimeout(() => {
      setSettling((cur) => (cur === key ? null : cur));
    }, ms);
  }, []);

  const executeTransition = useCallback(
    async (
      issue: BoardIssue,
      targetCat: BoardColumnKey,
      transition: JiraTransitionOption,
      origin: BoardIssue
    ) => {
      // Optimistic final status + settle immediately; snap-back clears settle on failure.
      // Bump `updated` so Done's 14d window includes the card (stale Done would vanish).
      patchIssue(issue.key, {
        statusCategory: targetCat,
        status: transition.toStatus,
        displayStatus: undefined,
        ...(targetCat === "done" ? { updated: new Date().toISOString() } : {}),
      });
      markSettling(issue.key);
      try {
        await projectJiraAction(projectId, issue.key, {
          action: "transition",
          transitionId: transition.id,
          toStatus: transition.toStatus,
        });
        toastRef.current({
          text: `${issue.key} → ${transition.toStatus} in Jira`,
          tone: "ok",
        });
      } catch (e) {
        restoreOrigin(origin);
        clearSettling();
        const reason = e instanceof Error ? e.message : "Transition failed";
        toastRef.current({
          text: `${issue.key} snapped back — ${reason}`,
          tone: "bad",
          retry: { key: issue.key, targetCat, message: reason },
        });
      }
    },
    [clearSettling, markSettling, patchIssue, projectId, restoreOrigin]
  );

  const runDrop = useCallback(
    async (key: string, targetCat: BoardColumnKey) => {
      const issue = issues.find((i) => i.key === key);
      if (!issue) return;
      const originCat = String(issue.statusCategory ?? "").toLowerCase() as BoardColumnKey;
      if (originCat === targetCat) return;

      const origin: BoardIssue = { ...issue, displayStatus: undefined };

      // In-flight: Moving… only — settle chrome waits for a chosen transition.
      // Bump `updated` on Done so recentDone's 14d filter doesn't drop the card.
      patchIssue(key, {
        statusCategory: targetCat,
        displayStatus: "Moving…",
        ...(targetCat === "done" ? { updated: new Date().toISOString() } : {}),
      });
      setPending(null);
      clearSettling();

      let transitions: JiraTransitionOption[] = [];
      try {
        transitions = await getProjectJiraTransitions(projectId, key);
      } catch (e) {
        restoreOrigin(origin);
        clearSettling();
        const reason = e instanceof Error ? e.message : "Could not load transitions";
        toastRef.current({
          text: `${key} snapped back — ${reason}`,
          tone: "bad",
          retry: { key, targetCat, message: reason },
        });
        return;
      }

      const catMap = buildStatusCatMap(issues);
      const matches = transitions.filter((t) => transitionMatchesColumn(t, targetCat, catMap));

      if (matches.length === 0) {
        restoreOrigin(origin);
        clearSettling();
        const named =
          transitions.length > 0
            ? transitions.map((t) => t.toStatus).join(", ")
            : "no valid transitions";
        const msg = `Jira has no ${origin.status} → ${COLUMN_LABEL[targetCat]} transition (${named})`;
        toastRef.current({
          text: `${key} snapped back — ${msg}`,
          tone: "bad",
          retry: { key, targetCat, message: msg },
        });
        return;
      }

      if (matches.length === 1) {
        await executeTransition(issue, targetCat, matches[0], origin);
        return;
      }

      // Ambiguous: Moving… + Popper only — never settle chrome / silent guess.
      setPending({ key, targetCat, options: matches, origin });
    },
    [clearSettling, executeTransition, issues, patchIssue, projectId, restoreOrigin]
  );

  const onDragStart = useCallback((key: string) => {
    setDragKey(key);
  }, []);

  const onDragEnd = useCallback(() => {
    setDragKey(null);
    setDragOver(null);
  }, []);

  const onDragOverCol = useCallback((cat: BoardColumnKey, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOver((cur) => (cur === cat ? cur : cat));
  }, []);

  const onDragLeaveCol = useCallback(() => {
    setDragOver(null);
  }, []);

  const onDropCol = useCallback(
    (cat: BoardColumnKey, e: React.DragEvent) => {
      e.preventDefault();
      const key = dragKey ?? e.dataTransfer.getData("text/plain");
      setDragOver(null);
      setDragKey(null);
      if (!key) return;
      void runDrop(key, cat);
    },
    [dragKey, runDrop]
  );

  const pickTransition = useCallback(
    (transition: JiraTransitionOption) => {
      if (!pending) return;
      const { key, targetCat, origin } = pending;
      setPending(null);
      const issue = issues.find((i) => i.key === key) ?? { ...origin, statusCategory: targetCat };
      void executeTransition(issue, targetCat, transition, origin);
    },
    [executeTransition, issues, pending]
  );

  const retryDrop = useCallback(
    (failed: FailedDrop) => {
      void runDrop(failed.key, failed.targetCat);
    },
    [runDrop]
  );

  return {
    dragKey,
    dragOver,
    settling,
    pending,
    onDragStart,
    onDragEnd,
    onDragOverCol,
    onDragLeaveCol,
    onDropCol,
    pickTransition,
    retryDrop,
  };
}
